import React, { useMemo, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import Accordion from "@components/Accordion/Accordion";
import { Alert } from "@components/Alert/Alert";
import Button from "@components/Button/Button";
import ErrorHandler from "@components/ErrorHandler/ErrorHandler";
import FormDivider from "@components/FormDivider/FormDivider";
import FormItem from "@components/FormItem/FormItem";
import LoadingOverlay from "@components/LoadingOverlay/LoadingOverlay";
import { useNotification } from "@context/NotificationContext";
import { zodResolver } from "@hookform/resolvers/zod";
import type { GateReason, StagedAttachment } from "@services/drybulkCargoOperationsApi";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { z } from "zod";
import {
  DRYBULK_CARGO_OPS_LANG_BASE,
  DRYBULK_CARGO_OPS_ROUTE_SEGMENTS,
  PLAN_ATTACHMENT_TYPES,
  type PlanAttachmentType,
} from "../constants";
import { useAddOperationGate } from "../hooks/useAddOperationGate";
import { useAttachStagedPlans } from "../hooks/useAttachStagedPlans";
import { useCachedCargoOperationDetail } from "../hooks/useCargoOperationDetail";
import { useCreateCargoOperation } from "../hooks/useCargoOperationMutations";
import { useVesselCallOperationContext } from "../hooks/useVesselCallOperationContext";
import {
  cargoOpsDetailPath,
  cargoOpsImportDeferredPath,
  isInCargoOpsTree,
} from "../utils/cargoOpsPaths";
import { newOperationId } from "../utils/newOperationId";
import { problemDetail, problemStatus } from "../utils/problemDetail";
import DraftActions from "./DraftActions";
import NominationLinks from "./NominationLinks";
import PlanAttachmentSlot from "./PlanAttachmentSlot";
import SequenceSection from "./SequenceSection";
import TotalQuantityEditor, { type TotalQuantityEditorHandle } from "./TotalQuantityEditor";

const L = `${DRYBULK_CARGO_OPS_LANG_BASE}.add`;

const SERVICE_UNAVAILABLE = 503;

const GATE_REASON_KEYS: Record<GateReason, string> = {
  VESSEL_CALL_UNAVAILABLE: `${L}.gate-reason.VESSEL_CALL_UNAVAILABLE`,
  VESSEL_CALL_NOT_APPROVED: `${L}.gate-reason.VESSEL_CALL_NOT_APPROVED`,
  NO_APPROVED_NOMINATION: `${L}.gate-reason.NO_APPROVED_NOMINATION`,
  NOT_LINKED_TO_CALL: `${L}.gate-reason.NOT_LINKED_TO_CALL`,
  NOT_DRY_BULK: `${L}.gate-reason.NOT_DRY_BULK`,
  OTHER_COMPANY: `${L}.gate-reason.OTHER_COMPANY`,
  NOMINATION_LOOKUP_UNAVAILABLE: `${L}.gate-reason.NOMINATION_LOOKUP_UNAVAILABLE`,
};

const gateReasonKey = (reasonCode: string | null | undefined): string | undefined =>
  reasonCode && Object.keys(GATE_REASON_KEYS).includes(reasonCode)
    ? GATE_REASON_KEYS[reasonCode as GateReason]
    : undefined;

const TOTAL_QUANTITY_PATTERN = /^\d{1,11}(\.\d{1,3})?$/;

const addOperationSchema = (t: (key: string) => string) =>
  z.object({
    operationType: z.enum(["", "LOAD", "UNLOAD"]),
    totalQuantity: z
      .string()
      .trim()
      .regex(TOTAL_QUANTITY_PATTERN, t(`${L}.validation.total-quantity`)),
  });
type FormValues = z.infer<ReturnType<typeof addOperationSchema>>;

interface SavedOperation {
  id: string;
  status: string | null;
  totalQuantity: number | null;
  remained: number | null;
}

/** No polling (the sequence section is mounted with `poll={false}`), no client arithmetic (HLD 5.3), nothing rendered that the BFF did not send (R-34). */
const AddCargoOperationPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { notify } = useNotification();
  const inCargoOpsTree = isInCargoOpsTree(useLocation().pathname);
  const { vesselCallId, vesselImo } = useVesselCallOperationContext();
  const gate = useAddOperationGate(vesselImo, vesselCallId);
  const createOperation = useCreateCargoOperation();
  const [saved, setSaved] = useState<SavedOperation | null>(null);
  const cachedDetail = useCachedCargoOperationDetail(saved?.id);
  const committing = useRef(false);
  const totalRef = useRef<TotalQuantityEditorHandle>(null);
  const [operationId] = useState(newOperationId);
  // DBCO-0030: plans staged in Blob and waiting for Save as draft / Add Operation. Page state only: nothing persists.
  const [stagedPlans, setStagedPlans] = useState<
    Record<PlanAttachmentType, StagedAttachment | null>
  >({ LOADING_PLAN: null, STOWAGE_PLAN: null });
  const { attachStaged } = useAttachStagedPlans({
    onAttached: (type) => setStagedPlans((prev) => ({ ...prev, [type]: null })),
  });
  const attachStagedPlans = async () => {
    const pending = PLAN_ATTACHMENT_TYPES.map((type) => stagedPlans[type]).filter(
      (plan): plan is StagedAttachment => plan !== null,
    );
    return saved && pending.length > 0 ? attachStaged(saved.id, pending) : true;
  };
  const attachedPlan = (type: PlanAttachmentType) =>
    (cachedDetail.data?.attachments ?? []).filter((a) => a.attachmentType === type).pop() ?? null;

  const schema = useMemo(() => addOperationSchema(t), [t]);
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { operationType: "", totalQuantity: "" },
  });
  const { watch, handleSubmit } = form;
  const operationType = watch("operationType");

  const openImportDeferred = () =>
    inCargoOpsTree
      ? navigate(cargoOpsImportDeferredPath())
      : navigate(`../${DRYBULK_CARGO_OPS_ROUTE_SEGMENTS.importDeferred}`, { relative: "path" });

  const openOperation = (operationId: string) =>
    inCargoOpsTree
      ? navigate(cargoOpsDetailPath(operationId))
      : navigate(`../${operationId}`, { relative: "path" });

  const commit = handleSubmit(async ({ totalQuantity }) => {
    if (committing.current || saved || !vesselCallId || !vesselImo) {
      return;
    }
    committing.current = true;
    try {
      const created = await createOperation.mutateAsync({
        id: operationId,
        vesselCallId,
        vesselImo,
        operationType: "LOAD",
        totalQuantity: Number(totalQuantity),
      });
      setSaved({
        id: created.id,
        status: created.status,
        totalQuantity: created.totalQuantity,
        remained: created.totalQuantityRemained,
      });
    } catch (error) {
      notify(
        problemStatus(error) === SERVICE_UNAVAILABLE
          ? {
              type: "error",
              title: t(`${L}.create-unavailable-title`),
              description: t(`${L}.create-unavailable-description`),
            }
          : {
              type: "error",
              title: t("common.error"),
              description: problemDetail(error) ?? t(`${L}.save-failed`),
            },
      );
    } finally {
      committing.current = false;
    }
  });

  if (!vesselCallId || !vesselImo) {
    return (
      <Alert type="info">
        {t(inCargoOpsTree ? `${L}.add-missing-context` : `${L}.no-vessel-call`)}
      </Alert>
    );
  }
  if (gate.isLoading) {
    return (
      <div className="relative min-h-[240px]">
        <LoadingOverlay loading testId="drybulk-add-operation-gate-loading" />
      </div>
    );
  }
  if (gate.isError) {
    return <ErrorHandler error={gate.error} testId="drybulk-add-operation-gate-error" />;
  }
  if (!gate.data?.enabled) {
    const reasonKey = gateReasonKey(gate.data?.reasonCode);
    return <Alert type="warning">{reasonKey ? t(reasonKey) : gate.data?.reason}</Alert>;
  }

  const nominations = gate.data.nominations ?? [];

  return (
    <FormProvider {...form}>
      <Box
        data-testid="drybulk-add-operation"
        sx={{ display: "flex", flexDirection: "column", gap: 2 }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
          <Typography variant="h5" component="h2">
            {t(`${L}.title`)}
          </Typography>
          <NominationLinks nominations={nominations} />
        </Box>

        <Box sx={{ maxWidth: 360 }}>
          <FormItem<FormValues, "operationType">
            name="operationType"
            type="select"
            label={t(`${L}.operation-type`)}
            testId="drybulk-operation-type"
            disabled={saved !== null}
            options={[
              { value: "LOAD", label: t(`${L}.operation-type-loading`) },
              { value: "UNLOAD", label: t(`${L}.operation-type-unloading`) },
            ]}
            onFieldChange={(_key, value) => {
              if (value === "UNLOAD") {
                openImportDeferred();
              }
            }}
          />
        </Box>

        {operationType === "LOAD" && (
          <Accordion
            testId="drybulk-operation-info"
            summary={t(`${L}.operation-info`)}
            defaultExpanded
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {saved ? (
                <TotalQuantityEditor
                  ref={totalRef}
                  operationId={saved.id}
                  totalQuantity={cachedDetail.data?.totalQuantity ?? saved.totalQuantity}
                  remained={cachedDetail.data?.totalQuantityRemained ?? saved.remained}
                  editable
                  onSaved={(updated) =>
                    setSaved({
                      id: saved.id,
                      status: saved.status,
                      totalQuantity: updated.totalQuantity,
                      remained: updated.totalQuantityRemained,
                    })
                  }
                />
              ) : (
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                  <Box
                    sx={{ width: 280 }}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        event.preventDefault();
                        void commit();
                      }
                    }}
                  >
                    <FormItem<FormValues, "totalQuantity">
                      name="totalQuantity"
                      type="text"
                      label={t(`${L}.total-quantity`)}
                      testId="drybulk-total-quantity"
                      slotProps={{ htmlInput: { inputMode: "decimal" } }}
                    />
                  </Box>
                  <Box sx={{ display: "flex", gap: 1, pt: 1 }}>
                    <Button
                      variant="contained"
                      onClick={() => void commit()}
                      label={
                        <span className="normal-case font-medium">
                          {t(`${L}.add-total-quantity`)}
                        </span>
                      }
                    />
                  </Box>
                </Box>
              )}
            </Box>
          </Accordion>
        )}

        {operationType === "LOAD" &&
          (saved ? (
            <>
              <SequenceSection operationId={saved.id} poll={false} />
            </>
          ) : (
            <Box>
              <Button
                variant="outlined"
                disabled
                label={<span className="normal-case font-medium">{t(`${L}.add-sequence`)}</span>}
              />
            </Box>
          ))}

        {operationType === "LOAD" && (
          <Box data-testid="drybulk-plan-slots" sx={{ display: "flex", flexDirection: "column" }}>
            {PLAN_ATTACHMENT_TYPES.map((type) => (
              <PlanAttachmentSlot
                key={type}
                attachmentType={type}
                staged={stagedPlans[type]}
                onStagedChange={(next) => setStagedPlans((prev) => ({ ...prev, [type]: next }))}
                attached={attachedPlan(type)}
              />
            ))}
          </Box>
        )}

        <Box sx={{ mt: 2 }}>
          <FormDivider />
        </Box>
        <DraftActions
          operationId={saved?.id ?? null}
          status={cachedDetail.data?.status ?? saved?.status ?? null}
          onLeave={() => navigate("..", { relative: "path" })}
          onSubmitted={() => saved && openOperation(saved.id)}
          discardOnCancel
          hasChanges={saved !== null && (cachedDetail.data?.status ?? saved.status) === "DRAFT"}
          completeLabelKey={`${DRYBULK_CARGO_OPS_LANG_BASE}.actions.add-operation`}
          beforeSaveDraft={async () =>
            ((await totalRef.current?.commitPending()) ?? true) && (await attachStagedPlans())
          }
          beforeComplete={attachStagedPlans}
        />
      </Box>
    </FormProvider>
  );
};

export default AddCargoOperationPage;
